import { Link } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  MessageCircle,
  BookOpen,
  Mic,
  Users,
  BarChart2,
  Gift,
  ArrowRight,
  ChevronLeft,
  Zap,
  PenLine,
} from "lucide-react";

const tiers = ["Free", "Pro", "Pro+", "Family"] as const;

type Tier = (typeof tiers)[number];

const CHECK: Record<Tier, React.ReactNode> = {
  Free: <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />,
  Pro: <CheckCircle className="h-5 w-5 text-indigo-500 mx-auto" />,
  "Pro+": <CheckCircle className="h-5 w-5 text-pink-500 mx-auto" />,
  Family: <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />,
};

const TICK = (tier: Tier) => CHECK[tier];
const CROSS = <XCircle className="h-5 w-5 text-slate-300 mx-auto" />;

interface Row {
  label: string;
  values: Record<Tier, React.ReactNode>;
}

const rows: Row[] = [
  {
    label: "Coach (ZillAi) messages / month",
    values: { Free: "20", Pro: "150", "Pro+": "300", Family: "150 each" },
  },
  {
    label: "Custom lessons / month",
    values: { Free: "3", Pro: "20", "Pro+": "40", Family: "20 each" },
  },
  {
    label: "Premium AI voices (Chirp3-HD)",
    values: { Free: CROSS, Pro: TICK("Pro"), "Pro+": TICK("Pro+"), Family: TICK("Family") },
  },
  {
    label: "AI-recommended lessons",
    values: { Free: CROSS, Pro: TICK("Pro"), "Pro+": TICK("Pro+"), Family: TICK("Family") },
  },
  {
    label: "Daily challenges (crossword, word search & matching)",
    values: { Free: TICK("Free"), Pro: TICK("Pro"), "Pro+": TICK("Pro+"), Family: TICK("Family") },
  },
  {
    label: "Lesson Studio (parent-created lessons)",
    values: { Free: CROSS, Pro: CROSS, "Pro+": CROSS, Family: TICK("Family") },
  },
  {
    label: "Unlimited journal entries",
    values: { Free: TICK("Free"), Pro: TICK("Pro"), "Pro+": TICK("Pro+"), Family: TICK("Family") },
  },
  {
    label: "Accomplishment wall",
    values: { Free: TICK("Free"), Pro: TICK("Pro"), "Pro+": TICK("Pro+"), Family: TICK("Family") },
  },
  {
    label: "Resilience dashboard",
    values: { Free: TICK("Free"), Pro: TICK("Pro"), "Pro+": TICK("Pro+"), Family: TICK("Family") },
  },
  {
    label: "Early access to new features",
    values: { Free: CROSS, Pro: CROSS, "Pro+": TICK("Pro+"), Family: CROSS },
  },
  {
    label: "Parent portal",
    values: { Free: CROSS, Pro: CROSS, "Pro+": CROSS, Family: TICK("Family") },
  },
  {
    label: "Assign lessons to children",
    values: { Free: CROSS, Pro: CROSS, "Pro+": CROSS, Family: TICK("Family") },
  },
  {
    label: "Track children's progress",
    values: { Free: CROSS, Pro: CROSS, "Pro+": CROSS, Family: TICK("Family") },
  },
  {
    label: "Admin bonus messages",
    values: { Free: CROSS, Pro: TICK("Pro"), "Pro+": TICK("Pro+"), Family: TICK("Family") },
  },
];

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  tiers: string;
}

const features: Feature[] = [
  {
    icon: <MessageCircle className="h-6 w-6 text-indigo-500" />,
    title: "Coach Messages (ZillAi)",
    description:
      "ZillAi is your personal AI resilience coach. Each back-and-forth message exchange counts as one interaction. Use them to process a hard day, prep for a tough conversation, reflect on a lesson, or just talk through what you're feeling. When you hit your monthly limit the coach goes quiet until the next month resets — upgrading gives you significantly more time with your coach.",
    tiers: "Free: 20 · Pro: 150 · Pro+: 300 · Family: 150 per member",
  },
  {
    icon: <BookOpen className="h-6 w-6 text-indigo-500" />,
    title: "Custom Lessons",
    description:
      "Custom lessons let you build a lesson on any topic you choose — your coach generates a full interactive module with audio, reflection prompts, and a quiz tailored to you. Great for diving deeper into something you encountered in a catalog lesson or tackling a situation happening right now in your life. Each lesson you create counts toward your monthly limit.",
    tiers: "Free: 3 · Pro: 20 · Pro+: 40 · Family: 20 per member (parent can also assign up to 10 parent-created lessons/month to children)",
  },
  {
    icon: <Mic className="h-6 w-6 text-indigo-500" />,
    title: "Premium AI Voices (Chirp3-HD)",
    description:
      "All plans get audio lessons voiced by Google's standard TTS engine — clear and functional. Pro, Pro+, and Family unlock 12 additional Chirp3-HD neural voices: studio-quality, expressive speech that makes longer lessons significantly more engaging. You can choose your preferred voice and even switch coach persona voices in settings.",
    tiers: "Free: 6 standard voices · Pro / Pro+ / Family: 18 voices incl. 12 Chirp3-HD premium",
  },
  {
    icon: <Zap className="h-6 w-6 text-indigo-500" />,
    title: "AI-Recommended Lessons",
    description:
      "Rather than scrolling the full catalog, paid plans get a personalized weekly recommendation feed. ZillAi analyzes your lesson history, journal themes, resilience scores, and coach conversations to surface the lessons most relevant to where you are right now. Recommendations refresh each week.",
    tiers: "Pro, Pro+, Family only",
  },
  {
    icon: <BarChart2 className="h-6 w-6 text-indigo-500" />,
    title: "Resilience Dashboard, Journal & Daily Challenges",
    description:
      "These core features are available on every plan — free forever. Your resilience dashboard tracks your scores across domains like self-regulation, growth mindset, and social connection over time. The journal is completely private (no one — not even parents on Family plans — can read your entries). Every day you get three rotating challenge activities: a crossword puzzle, a word search, and a matching game — all built around real resilience and life-skills vocabulary.",
    tiers: "All plans",
  },
  {
    icon: <PenLine className="h-6 w-6 text-indigo-500" />,
    title: "Lesson Studio (Family Plan)",
    description:
      "Family plan parents get access to the Lesson Studio — a dedicated tool for creating custom lessons specifically for their children. Design a lesson around something happening in your child's life right now, set the topic and tone, and ZillAi generates a full interactive module with audio, reflection prompts, and a quiz. Studio-created lessons are assigned directly from the parent portal and don't count against the child's own monthly lesson limit (up to 10 parent-created lessons per month).",
    tiers: "Family plan only",
  },
  {
    icon: <Users className="h-6 w-6 text-indigo-500" />,
    title: "Parent Portal (Family Plan)",
    description:
      "The Family plan adds a dedicated parent dashboard where you can view your children's lesson completions, resilience scores, and learning streaks. You can assign specific lessons to each child, invite existing ZillHub accounts to join your family plan, or create new child accounts. Journals remain completely private to each child — parents receive general growth suggestions without seeing what their child has written.",
    tiers: "Family plan only · $14/month base (parent + 1 child) · $5/month per additional child",
  },
  {
    icon: <Gift className="h-6 w-6 text-indigo-500" />,
    title: "Admin Bonus Messages",
    description:
      "On paid plans, the ZillHub team can grant you extra coach messages on top of your monthly limit — for example as part of a referral program, a promotional offer, or if there was a service disruption. Bonus messages are applied to your current month and stack on top of your regular allowance. Free accounts are not eligible for bonus messages.",
    tiers: "Pro, Pro+, Family only",
  },
];

const tierColors: Record<Tier, string> = {
  Free: "text-slate-700",
  Pro: "text-indigo-700",
  "Pro+": "text-pink-700",
  Family: "text-green-700",
};

const tierHeaderColors: Record<Tier, string> = {
  Free: "bg-slate-50",
  Pro: "bg-indigo-50",
  "Pro+": "bg-pink-50",
  Family: "bg-green-50",
};

const ctaLinks: Record<Tier, { label: string; to: string; className: string }> = {
  Free: {
    label: "Start Free",
    to: "/login",
    className:
      "block w-full text-center px-4 py-2 text-sm font-bold text-indigo-600 border-2 border-indigo-600 rounded-full hover:bg-indigo-50 transition",
  },
  Pro: {
    label: "Go Pro — $9/mo",
    to: "/login",
    className:
      "block w-full text-center px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition",
  },
  "Pro+": {
    label: "Go Pro+ — $18/mo",
    to: "/login",
    className:
      "block w-full text-center px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-pink-600 to-purple-600 rounded-full hover:from-pink-700 hover:to-purple-700 transition",
  },
  Family: {
    label: "Go Family — $14/mo",
    to: "/login",
    className:
      "block w-full text-center px-4 py-2 text-sm font-bold text-white bg-green-600 rounded-full hover:bg-green-700 transition",
  },
};

export default function ComparePlans() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link
            to="/#pricing"
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 transition"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Pricing
          </Link>
          <span className="text-slate-300">|</span>
          <span className="text-slate-700 font-semibold">Compare Plans</span>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-14">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4">
            Everything, side by side
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            See exactly what each plan includes and pick the one that fits your goals.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm mb-20">
          <table className="w-full text-sm">
            {/* Tier headers */}
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left px-6 py-5 bg-white font-semibold text-slate-500 w-2/5">
                  Feature
                </th>
                {tiers.map((tier) => (
                  <th
                    key={tier}
                    className={`text-center px-4 py-5 w-[15%] ${tierHeaderColors[tier]}`}
                  >
                    <span className={`block text-lg font-extrabold ${tierColors[tier]}`}>
                      {tier}
                    </span>
                    {tier === "Family" ? (
                      <span className="block text-xs text-slate-600 font-normal mt-0.5 leading-snug">
                        $14/mo · parent + 1 child<br />
                        +$5/mo per additional child
                      </span>
                    ) : (
                      <span className="block text-xs text-slate-600 font-normal mt-0.5">
                        {tier === "Free" && "Free forever"}
                        {tier === "Pro" && "$9 / month"}
                        {tier === "Pro+" && "$18 / month"}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 bg-white">
              {rows.map((row) => (
                <tr key={row.label} className="hover:bg-slate-50 transition">
                  <td className="px-6 py-4 font-medium text-slate-700">{row.label}</td>
                  {tiers.map((tier) => (
                    <td
                      key={tier}
                      className={`text-center px-4 py-4 ${tierHeaderColors[tier]} bg-opacity-30`}
                    >
                      {typeof row.values[tier] === "string" ? (
                        <span className={`font-semibold ${tierColors[tier]}`}>
                          {row.values[tier]}
                        </span>
                      ) : (
                        row.values[tier]
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>

            {/* CTA row */}
            <tfoot>
              <tr className="border-t-2 border-slate-200">
                <td className="px-6 py-5 bg-white" />
                {tiers.map((tier) => (
                  <td key={tier} className={`px-4 py-5 ${tierHeaderColors[tier]} bg-opacity-30`}>
                    <Link to={ctaLinks[tier].to} className={ctaLinks[tier].className}>
                      {ctaLinks[tier].label}
                    </Link>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Feature explainers */}
        <div className="mb-12">
          <h2 className="text-3xl font-extrabold text-slate-900 mb-2 text-center">
            What each feature means
          </h2>
          <p className="text-slate-500 text-center mb-12">
            Plain-language explanations so you know exactly what you're getting.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((f) => (
              <div
                key={f.title}
                className="bg-white rounded-2xl border border-slate-200 p-7 shadow-sm hover:shadow-md transition"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="rounded-xl bg-indigo-50 p-2.5">{f.icon}</div>
                  <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{f.description}</p>
                <p className="text-xs font-semibold text-indigo-600 bg-indigo-50 rounded-lg px-3 py-1.5 inline-block">
                  {f.tiers}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center py-16 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl text-white px-8">
          <h2 className="text-3xl font-extrabold mb-3">Ready to level up?</h2>
          <p className="text-indigo-200 mb-8 max-w-xl mx-auto">
            Start free — no credit card required. Upgrade any time from your profile.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-bold text-indigo-600 bg-white rounded-full hover:bg-gray-50 shadow-xl transition"
            >
              Start Free <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/#pricing"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-bold text-white border-2 border-white/50 rounded-full hover:bg-white/10 transition"
            >
              See Pricing
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center py-8 text-xs text-slate-600">
        <p>
          Questions?{" "}
          <a href="mailto:support@zillhub.com" className="underline hover:text-indigo-600">
            support@zillhub.com
          </a>
        </p>
      </footer>
    </div>
  );
}
